// Estado de conectividad (F4 #52). Fuente única de verdad de si la app está «online» para
// decidir el camino de una mutación (online → REST directo; offline → outbox) y para pintar el
// indicador de la cabecera.
//
// navigator.onLine es NECESARIO pero no suficiente: una wifi «conectada» sin salida a Internet
// (o el backend caído) reporta onLine=true. Por eso se refuerza con un heartbeat periódico a
// /healthz (exento de auth/NDA en el backend): online = navigator.onLine && el último heartbeat
// respondió OK. La transición offline→online (confirmada por heartbeat) dispara el flush de la
// outbox al reconectar.
//
// FAIL-CLOSED AL ARRANCAR (#237): antes del primer heartbeat la conectividad está SIN CONFIRMAR y
// cuenta como offline. La app no se declara en línea por optimismo: mientras no hay confirmación
// no habilita lo que exige conexión ni empuja la outbox, y el indicador dice «Comprobando…». La
// primera confirmación OK es, a todos los efectos, una reconexión (dispara onReconnect).

import { useSyncExternalStore } from "react";

const HEARTBEAT_INTERVAL_MS = 20_000; // cadencia del sondeo de conectividad
const HEARTBEAT_TIMEOUT_MS = 5_000; // un /healthz que no responde en 5 s se trata como caído
const HEALTHZ_URL = "/healthz";

/** EstadoConexion distingue el arranque sin confirmar de la conectividad ya resuelta (#237). */
export type EstadoConexion = "sin_confirmar" | "online" | "offline";

// Estado del módulo. `heartbeat` es TRI-ESTADO: null = aún sin sondear (sin confirmar), true =
// último /healthz OK, false = caído. De ahí salen los dos valores observables: getOnline()
// (booleano para los gates y el camino de las mutaciones) y getEstadoConexion() (para pintar).
let navigatorOnline = typeof navigator === "undefined" ? true : navigator.onLine;
let heartbeat: boolean | null = null;

const listeners = new Set<() => void>();
let onReconnectCb: (() => void) | null = null;

/**
 * getOnline devuelve el estado de conectividad actual: navigator.onLine CONFIRMADO por heartbeat.
 * Sin confirmación (heartbeat === null) es false — fail-closed, nunca se supone red.
 */
export function getOnline(): boolean {
  return navigatorOnline && heartbeat === true;
}

/**
 * getEstadoConexion desglosa la conectividad en tres valores. Sin red del sistema operativo es
 * «offline» sin más (no hay nada que confirmar); con red pero sin heartbeat todavía, «sin
 * confirmar»; y ya sondeada, «online» u «offline» según el resultado.
 */
export function getEstadoConexion(): EstadoConexion {
  if (!navigatorOnline) {
    return "offline";
  }
  if (heartbeat === null) {
    return "sin_confirmar";
  }
  return heartbeat ? "online" : "offline";
}

/** subscribeOnline registra un oyente de cambios de conectividad; devuelve la baja. */
export function subscribeOnline(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

// Últimos valores notificados, para no despertar a los oyentes sin cambio observable.
let ultimoOnline = getOnline();
let ultimoEstado = getEstadoConexion();

// recompute recalcula los dos valores observables y, SOLO si cambió alguno, notifica a los
// oyentes. Una transición a online (la PRIMERA confirmación también lo es) dispara además el
// callback de reconexión (flush de la outbox).
function recompute(): void {
  const online = getOnline();
  const estado = getEstadoConexion();
  if (online === ultimoOnline && estado === ultimoEstado) {
    return;
  }
  const reconnected = online && !ultimoOnline;
  ultimoOnline = online;
  ultimoEstado = estado;
  for (const listener of listeners) {
    listener();
  }
  if (reconnected) {
    onReconnectCb?.();
  }
}

// ping sondea /healthz con timeout y actualiza el heartbeat. Si navigator ya está offline no
// gasta la petición (el estado ya es «offline», sin duda que resolver). Un fallo de red, una
// respuesta !ok o el timeout marcan el heartbeat como caído.
async function ping(): Promise<void> {
  if (!navigatorOnline) {
    return;
  }
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), HEARTBEAT_TIMEOUT_MS);
    try {
      const res = await fetch(HEALTHZ_URL, {
        method: "GET",
        cache: "no-store",
        signal: controller.signal,
      });
      heartbeat = res.ok;
    } finally {
      clearTimeout(timer);
    }
  } catch {
    heartbeat = false;
  }
  recompute();
}

/**
 * startConnectivityMonitor arranca la vigilancia de conectividad: eventos online/offline del
 * navegador + heartbeat periódico a /healthz. Lo monta el shell autenticado (AppLayout) y
 * devuelve una función de parada. `onReconnect` se invoca en cada transición confirmada
 * offline→online, incluida la PRIMERA confirmación (por eso el shell no necesita un flush
 * inicial propio: el arranque no sabe si hay red hasta que el heartbeat responde). Un monitor
 * nuevo exige confirmación nueva: releer navigator y descartar el heartbeat anterior; parar el
 * monitor también deja la conectividad sin confirmar. Al ir offline se marca el heartbeat como
 * caído para que volver a online exija un /healthz correcto antes de declarar reconexión.
 */
export function startConnectivityMonitor(opts?: { onReconnect?: () => void }): () => void {
  onReconnectCb = opts?.onReconnect ?? null;
  navigatorOnline = typeof navigator === "undefined" ? true : navigator.onLine;
  heartbeat = null;
  recompute();

  const onOffline = (): void => {
    navigatorOnline = false;
    heartbeat = false;
    recompute();
  };
  const onOnline = (): void => {
    navigatorOnline = true;
    recompute(); // sigue offline hasta que el heartbeat confirme
    void ping();
  };

  window.addEventListener("offline", onOffline);
  window.addEventListener("online", onOnline);
  const interval = setInterval(() => void ping(), HEARTBEAT_INTERVAL_MS);
  void ping(); // confirma conectividad real al arrancar

  return () => {
    window.removeEventListener("offline", onOffline);
    window.removeEventListener("online", onOnline);
    clearInterval(interval);
    // Sin monitor no hay heartbeat que sostenga la afirmación «hay red»: vuelve a sin confirmar.
    heartbeat = null;
    recompute();
    onReconnectCb = null;
  };
}

/** useOnlineStatus expone el estado de conectividad a los componentes (re-render en cada cambio). */
export function useOnlineStatus(): boolean {
  return useSyncExternalStore(subscribeOnline, getOnline, () => false);
}

/** useEstadoConexion expone el estado tri-estado a los componentes (re-render en cada cambio). */
export function useEstadoConexion(): EstadoConexion {
  return useSyncExternalStore(subscribeOnline, getEstadoConexion, () => "sin_confirmar");
}
